import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Button } from "../ui/button";
import { label as lbl } from "../../lib/format";

const SEL = "otms-input h-9 text-xs";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const Sel = ({ name, value, onChange, opts, placeholder }) => (
  <select className={SEL} value={value || ""} onChange={(e) => onChange(name, e.target.value)} data-testid={`filter-${name}`}>
    <option value="">{placeholder}</option>
    {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

function PeriodFields({ v, set, today }) {
  const year = Number((today || "2026").slice(0, 4));
  const years = Array.from({ length: 12 }, (_, i) => ({ value: String(year - i), label: String(year - i) }));
  return (
    <>
      <Sel name="year" value={v.year} onChange={set} opts={years} placeholder="Any year (use dates)" />
      {v.year && <Sel name="quarter" value={v.quarter} onChange={set} opts={[1, 2, 3, 4].map((q) => ({ value: String(q), label: `Q${q}` }))} placeholder="All quarters" />}
      {v.year && <Sel name="month" value={v.month} onChange={set} opts={MONTHS.map((m, i) => ({ value: String(i + 1), label: m }))} placeholder="All months" />}
      {!v.year && (
        <>
          <input type="date" className={SEL} value={v.date_from || ""} onChange={(e) => set("date_from", e.target.value)} data-testid="filter-date_from" aria-label="From date" />
          <input type="date" className={SEL} value={v.date_to || ""} onChange={(e) => set("date_to", e.target.value)} data-testid="filter-date_to" aria-label="To date" />
        </>
      )}
    </>
  );
}

function fieldFor(name, v, set, o) {
  const opts = {
    term_id: [o.terms, "All terms"], measure_type_id: [o.measure_types, "All measure types"], committee_id: [o.committees, "All committees"],
    sponsor_id: [o.sponsors, "All sponsors"], office_id: [o.offices, "All offices"], assigned_user_id: [o.users, "Any assignee"],
    stage: [o.stages, "All stages"], stage_category: [o.stage_categories.map((c) => ({ value: c, label: lbl(c) })), "All stage groups"],
    category: [["ordinance", "resolution", "other"].map((c) => ({ value: c, label: lbl(c) })), "Ordinances & resolutions"],
    disposition: [["in_progress", "returned", "approved", "archived", "withdrawn", "rejected"].map((c) => ({ value: c, label: lbl(c) })), "Any disposition"],
    publication_status: [[{ value: "recorded", label: "Posting recorded" }, { value: "none", label: "No posting recorded" }], "Any publication status"],
    effectivity_status: [["verified", "unverified", "none"].map((c) => ({ value: c, label: c === "none" ? "No effectivity record" : lbl(c) })), "Any effectivity status"],
    release_status: [["released", "pending_approval", "withdrawn", "rejected", "not_released"].map((c) => ({ value: c, label: lbl(c) })), "Any release status"],
  }[name];
  if (opts) return <Sel key={name} name={name} value={v[name]} onChange={set} opts={opts[0] || []} placeholder={opts[1]} />;
  if (name === "exclude_paused")
    return (
      <label key={name} className="flex items-center gap-2 text-xs text-slate-600">
        <input type="checkbox" checked={v.exclude_paused === "true"} onChange={(e) => set(name, e.target.checked ? "true" : "")} data-testid="filter-exclude_paused" />
        Exclude returned/paused time
      </label>
    );
  if (name === "due_within") return <input key={name} type="number" min="1" max="365" className={SEL} placeholder="Due within days" value={v.due_within || ""} onChange={(e) => set(name, e.target.value)} data-testid="filter-due_within" />;
  if (["q", "actor", "action", "entity_type"].includes(name))
    return <input key={name} className={SEL} placeholder={{ q: "Search text", actor: "User email", action: "Action prefix", entity_type: "Record type" }[name]} value={v[name] || ""} onChange={(e) => set(name, e.target.value)} data-testid={`filter-${name}`} />;
  return null;
}

export function FilterBar({ filters, values, options, onApply, onReset, extra }) {
  const [v, setV] = useState(values);
  useEffect(() => { setV(values); }, [values]);
  const set = (k, val) => setV((s) => ({ ...s, [k]: val, ...(k === "year" && val ? { date_from: "", date_to: "" } : {}), ...(k === "year" && !val ? { quarter: "", month: "" } : {}) }));
  return (
    <div className="mb-5 rounded-md border border-slate-200 bg-white p-4" data-testid="report-filter-bar">
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span className="font-semibold uppercase tracking-wider text-slate-600">{options.lgu.name}</span>
        <span>· {options.lgu.legislative_body || lbl(options.lgu.type)}</span>
      </div>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4 xl:grid-cols-6">
        {filters.includes("period") && <PeriodFields v={v} set={set} today={options.today} />}
        {filters.filter((f) => f !== "period").map((f) => fieldFor(f, v, set, options))}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => onApply(v)} data-testid="filter-apply-btn">Apply filters</Button>
        <Button size="sm" variant="outline" onClick={onReset} data-testid="filter-reset-btn">Reset</Button>
        {extra}
      </div>
    </div>
  );
}

export const ActiveFilters = ({ applied }) =>
  Object.keys(applied || {}).length ? (
    <div className="mb-4 flex flex-wrap gap-1.5" data-testid="active-filters">
      {Object.entries(applied).map(([k, val]) => (
        <span key={k} className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-0.5 text-[11px] text-sky-800 ring-1 ring-sky-200">
          <X className="h-3 w-3 opacity-50" />{k}: {String(val)}
        </span>
      ))}
    </div>
  ) : null;
